import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

import {
    Input,
    Label,
    SubmitButton,
    Textarea,
    Warning
} from '$partials/forms';
import { useLocale } from '$contexts/locale.jsx';
import Util from '$utilities/index';
import { useQueryParamsInSession } from '$utilities/storage';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
const formatPageUri = () => {
    if (window) {
        return window.location.hostname + window.location.pathname;
    }
    return undefined;
};

/**
 * @param {import('$types/components/partials/forms').ContactFormProps} props
 */
const ContactForm = ({ formConsentOptions, formFields, formId, formEmail, slug, title }) => {
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const utmParams = useQueryParamsInSession(utmKeys);

    const [errorMessage, setErrorMessage] = useState();
    const [message, setMessage] = useState();
    const { translate: T } = useLocale();
    const { register, handleSubmit, reset, formState: { errors, isDirty }, setValue, watch } = useForm();

    // Hubspot variables
    const dependentField = (formFields && formFields.status !== 'error') && formFields.filter(field => field.dependentFieldFilters.length > 0).map(field => field.dependentFieldFilters) || [];
    const utmFields = (formFields && formFields.status !== 'error') && formFields.filter(field => utmKeys.includes(field.name));
    const parentField = (formFields && formFields.status !== 'error') && formFields.filter(field => field.dependentFieldFilters.length > 0).map(field => `${field.objectTypeId}/${field.name}`);
    const watching = watch(parentField || []);
    const formatConsent = (data) => {
        if (formConsentOptions.type === 'legitimate_interest') {
            return {
                legitimateInterest: {
                    value: true,
                    // This must be true when using the 'legitimateInterest' option, as it reflects the consent indicated by the visitor when submitting the form
                    subscriptionTypeId: formConsentOptions.subscriptionTypeId[0],
                    // Integer; The ID of the specific subscription type that this forms indicates interest to.
                    legalBasis: formConsentOptions.lawfulBasis.toUpperCase(),
                    // String, one of CUSTOMER or LEAD; Whether this form indicates legitimate interest from a prospect/lead or from a client.
                    text: formConsentOptions.privacyText
                    // String; The privacy text displayed to the visitor.
                }
            };
        } else {
            const communicationResponses = Object.keys(data).filter(item => item.includes('communications/')).map(item => {
                const id = parseInt(item.replace('communications/', ''));
                return (
                    {
                        value: data[item],
                        subscriptionTypeId: id,
                        text: formConsentOptions.communicationsCheckboxes?.find(item => item.subscriptionTypeId === id)?.label
                    }
                );
            });
            return {
                consent: {
                    consentToProcess: data.consentToProcess,
                    text: formConsentOptions.consentToProcessCheckboxLabel || formConsentOptions.consentToProcessText,
                    // A list of details for the Consent to communicate for each subscription type included in the form
                    communications: communicationResponses
                }
            };
        }
    };
    // Hubspot submission
    const onHubSpotSubmit = async (data) => {
        setErrorMessage(null);
        setMessage(null);

        // replace null values with empty string so that form submits properly
        const hasNullValue = (obj) => {
            for (let key in obj) {
                if (obj[key] === null) {
                    return obj[key] = '';
                }
            }
            return false;
        };
        hasNullValue(data);

        let legalObject = null;
        if (formConsentOptions) {
            legalObject = formatConsent(data);
            const communicationResponses = Object.keys(data).filter(item => item.includes('communications/'));
            communicationResponses.forEach(item => delete data[item]);
            delete data.consentToProcess;
        }
        const formBody = formConsentOptions ? {
            formId: formId,
            fields: {
                ...data
            },
            legalConsentOptions: legalObject
        } : {
            formId: formId,
            fields: {
                ...data
            }
        };
        try {
            const res = await fetch('/api/crm/v1/form', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRF-Token': Util.cookies.getItem('crumb'),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...formBody,
                    context: {
                        hutk: window.sessionStorage.getItem('hubspot_hubspotutk') || undefined,
                        pageUri: formatPageUri(),
                        pageName: title
                    }
                })
            });

            if (res.status === 200) {
                const body = await res.json();
                if (body.redirectUri) {
                    const url = new URL(body.redirectUri);
                    window.location = url.href;
                }
                reset();
                setMessage(T('Thank you!'));
            } else {
                const data = await res.json();
                throw new Error(data?.message);
            }

        } catch (err) {
            console.error(err);
            setErrorMessage(T('Sorry, something went wrong. Please try again later.'));
        }
    };

    // Email submission
    const onEmailSubmit = async (data) => {
        setErrorMessage(null);
        setMessage(null);
        try {
            const res = await fetch('/api/v1/contact', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRF-Token': Util.cookies.getItem('crumb'),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    slug: slug,
                    fields: {
                        ...data
                    }
                })
            });

            if (res.status === 200) {
                reset();
                setMessage(T('Thank you!'));
            } else {
                const data = await res.json();
                throw new Error(data?.message);
            }
        } catch (err) {
            console.error(err);

            setErrorMessage(T('Sorry, something went wrong. Please try again later.'));
        }
    };

    // Keep UTM params values in sync with form data
    useEffect(() => {
        if (utmFields?.length > 0) {
            for (const field of utmFields) {
                if (utmParams?.[field?.name]) {
                    setValue(`${field.objectTypeId}/${field.name}`, utmParams[field.name]);
                }
            }
        }
    }, [utmParams]);

    const fieldMissing = Object.keys(errors).find((k) => errors[k].type === 'required');

    return (
        <>
            {/* Is a Hubspot form */}
            {formId && Array.isArray(formFields) && (
                <form
                    className='flex flex-wrap'
                    onSubmit={handleSubmit(onHubSpotSubmit)}>
                    {formFields.map((field, i) => {
                        if (field.hidden) {
                            return null;
                        }
                        const hiddenFields = dependentField.flat()?.map(field => {
                            const isActive = watching.flat().includes(field.filters?.[0]?.strValues?.[0]);
                            return (
                                isActive ? null : field.dependentFormField?.name
                            );
                        }) || [];

                        switch (field.fieldType) {
                            case 'text':
                            case 'number':
                                return (
                                    <div
                                        className={`w-full ${i > 4 ? '' : 'md:w-1/2'} px-8 mb-25 ${hiddenFields.includes(field.name) ? 'hidden' : ''}`}
                                        key={i}>
                                        <Label
                                            htmlFor={field.name}
                                            required={field.required && !hiddenFields.includes(field.name)}>
                                            {T(field.label)}
                                        </Label>
                                        <Input
                                            placeholder={T(field.placeholder)}
                                            {...register(`${field.objectTypeId}/${field.name}`, { required: field.required && !hiddenFields.includes(field.name) })} />
                                        {field.description !== '' && (
                                            <p
                                                className='type-style-single-line-small mt-6 text-text-tertiary mb-10'
                                                dangerouslySetInnerHTML={{ __html: field.description }}>
                                            </p>
                                        )}
                                    </div>
                                );
                            case 'radio':
                                return (
                                    <div
                                        className={`sm:mt-20 w-full px-8  mb-25 ${hiddenFields.includes(field.name) ? 'hidden' : ''}`}
                                        key={i}>
                                        <fieldset>
                                            <legend
                                                className='inline-block border-0 p-0 pb-15 type-style-10'>
                                                {T(field.label)}
                                                <sup className='text-brand-500 ml-5'>*</sup>
                                            </legend>
                                            {field?.options?.length > 0 && (
                                                field?.options?.map((option, i) => {
                                                    return (
                                                        <div
                                                            className='gap-15 flex flex-row items-center pb-10'
                                                            key={`${option?.label}-${i}`}>

                                                            <input
                                                                className='bg-surface-raised border-none'
                                                                id={option.label}
                                                                type='radio'
                                                                value={option.value}
                                                                {...register(`${field.objectTypeId}/${field.name}`, { required: field.required && !hiddenFields.includes(field.name) })} />
                                                            <label
                                                                className='inline-block border-0 p-0 type-style-body'
                                                                dangerouslySetInnerHTML={{ __html: T(option.label) }}
                                                                htmlFor={option.label} />
                                                        </div>
                                                    );
                                                })
                                            )}
                                            {field.description !== '' && (
                                                <p
                                                    className='type-style-single-line-small mt-6 text-text-tertiary mb-10'
                                                    dangerouslySetInnerHTML={{ __html: field.description }}>
                                                </p>
                                            )}
                                        </fieldset>
                                    </div>
                                );
                            case 'checkbox':
                                return (
                                    <div
                                        className={`sm:mt-20 w-full px-8 mb-25 ${hiddenFields.includes(field.name) ? 'hidden' : ''}`}
                                        key={i}>
                                        <fieldset>
                                            <legend
                                                className='inline-block border-0 p-0 pb-15 type-style-10'>
                                                {T(field.label)}
                                                <sup className='text-brand-500 ml-5'>*</sup>
                                            </legend>
                                            {field?.options?.length > 0 && (
                                                field?.options?.map((option, i) => {
                                                    return (
                                                        <div
                                                            className='gap-15 flex flex-row items-center pb-10'
                                                            key={`${option?.label}-${i}`}>

                                                            <input
                                                                className='bg-surface-raised border-none'
                                                                id={option.label}
                                                                type='checkbox'
                                                                value={option.value}
                                                                {...register(`${field.objectTypeId}/${field.name}`, { required: field.required && !hiddenFields.includes(field.name) })} />
                                                            <label
                                                                className='inline-block border-0 p-0 type-style-body'
                                                                dangerouslySetInnerHTML={{ __html: T(option.label) }}
                                                                htmlFor={option.label} />
                                                        </div>
                                                    );
                                                })
                                            )}
                                            {field.description !== '' && (
                                                <p
                                                    className='type-style-single-line-small mt-6 text-text-tertiary mb-10'
                                                    dangerouslySetInnerHTML={{ __html: field.description }}>
                                                </p>
                                            )}
                                        </fieldset>
                                    </div>
                                );
                            case 'booleancheckbox':
                                return (
                                    <div
                                        className={`sm:mt-20 w-full px-8 mb-25 ${hiddenFields.includes(field.name) ? 'hidden' : ''}`}
                                        key={i}>
                                        <fieldset>
                                            <legend className='inline-block border-0 p-0 pb-15 type-style-10'>
                                                {T(field.label)}
                                                <sup className='text-brand-500 ml-5'>*</sup>
                                            </legend>
                                            {field?.options?.length > 0 &&
                                                field.options.map((option, index) => (
                                                    <div
                                                        className='gap-15 flex flex-row items-center pb-10'
                                                        key={`${option?.label}-${index}`}>
                                                        <input
                                                            className='bg-surface-raised border-none'
                                                            id={`${field.name}-${option.value}`}
                                                            type='radio'
                                                            value={option.value}
                                                            {...register(`${field.objectTypeId}/${field.name}`, {
                                                                required: field.required && !hiddenFields.includes(field.name),
                                                            })} />
                                                        <label
                                                            className='inline-block border-0 p-0 type-style-body cursor-pointer'
                                                            dangerouslySetInnerHTML={{ __html: T(option.label) }}
                                                            htmlFor={`${field.name}-${option.value}`} />
                                                    </div>
                                                ))}
                                            {field.description !== '' && (
                                                <p
                                                    className='type-style-single-line-small mt-6 text-text-tertiary mb-10'
                                                    dangerouslySetInnerHTML={{ __html: field.description }}>
                                                </p>
                                            )}
                                        </fieldset>
                                    </div>
                                );
                            case 'select':
                                return (
                                    <div
                                        className={`w-full px-8 mb-25 ${hiddenFields.includes(field.name) ? 'hidden' : ''}`}
                                        key={i}>
                                        <Label
                                            htmlFor={field.name}
                                            required={field.required && !hiddenFields.includes(field.name)}>
                                            {T(field.label)}
                                        </Label>
                                        <select
                                            className='select w-full bg-surface-raised border-0 type-style-body rounded text-text-tertiary'
                                            id={field.name}
                                            name={field.name}
                                            {...register(`${field.objectTypeId}/${field.name}`, { required: field.required && !hiddenFields.includes(field.name) })}>
                                            {field.options.length > 0 && [{ value: null, label: T('Please Select') }].concat(field.options).map(option => {
                                                return (
                                                    <option
                                                        key={option.value}
                                                        value={option.value}>{T(option.label)}</option>
                                                );
                                            })}
                                        </select>
                                        {field.description !== '' && (
                                            <p
                                                className='type-style-single-line-small mt-6 text-text-tertiary mb-10'
                                                dangerouslySetInnerHTML={{ __html: field.description }}>
                                            </p>
                                        )}
                                    </div>
                                );
                            case 'textarea':
                                return (
                                    <div
                                        className={`w-full px-8 mb-25 ${hiddenFields.includes(field.name) ? 'hidden' : ''}`}
                                        key={i}>
                                        <Label
                                            htmlFor={field.name}
                                            required={field.required && !hiddenFields.includes(field.name)}>
                                            {T(field.label)}
                                        </Label>
                                        <Textarea
                                            placeholder={T(field.placeholder)}
                                            rows={4}
                                            {...register(`${field.objectTypeId}/${field.name}`, { required: field.required && !hiddenFields.includes(field.name) })} />
                                        {field.description !== '' && (
                                            <p
                                                className='type-style-single-line-small mt-6 text-text-tertiary mb-10'
                                                dangerouslySetInnerHTML={{ __html: field.description }}>
                                            </p>
                                        )}
                                    </div>
                                );
                            default:
                                return null;
                        }
                    })}

                    <div className='flex flex-wrap justify-between items-center w-full  sm:text-right'>
                        {fieldMissing &&
                            <Warning className='flex items-center type-style-eyebrow mb-25 mr-20'>
                                {T('FORM_ERROR_ONE_OR_MORE_REQUIRED_FIELDS')}
                            </Warning>
                        }

                        {(!fieldMissing && errorMessage) &&
                            <Warning className='flex items-center type-style-eyebrow mb-25 mr-20 '>
                                {errorMessage}
                            </Warning>
                        }

                        {(!fieldMissing && !errorMessage && !isDirty && message) &&
                            <div className='flex items-center type-style-eyebrow mb-25 mr-20'>
                                {message}
                            </div>
                        }
                    </div>
                    {formConsentOptions?.type !== 'none' && (
                        <div className='flex flex-col '>
                            {formConsentOptions?.communicationConsentText && (
                                <div className='flex flex-col mb-25'>
                                    <div
                                        className='w-full rich-text type-style-body font-semibold'
                                        dangerouslySetInnerHTML={{ __html: formConsentOptions.communicationConsentText }} />
                                    {formConsentOptions?.communicationsCheckboxes?.length > 0 && formConsentOptions.communicationsCheckboxes.map((option, i) => {
                                        return (
                                            <div
                                                className='gap-15 flex flex-row items-center pb-10 mt-20'
                                                key={`${option?.label}-${i}`}>

                                                <input
                                                    className='bg-surface-raised border-none'
                                                    id={option.label}
                                                    type='checkbox'
                                                    value={option.value}
                                                    {...register(`communications/${option.subscriptionTypeId}`, { required: option.required })} />
                                                <label
                                                    className='inline-block border-0 p-0 type-style-body'
                                                    htmlFor={option.label}>
                                                    {T(option.label)}
                                                    {option.required && (
                                                        <sup className='text-brand-500 ml-5'>*</sup>
                                                    )}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {formConsentOptions.consentToProcessText && (
                                <div
                                    className='gap-15 flex flex-row items-center mb-25 type-style-body'
                                    dangerouslySetInnerHTML={{ __html: formConsentOptions.consentToProcessText }} />
                            )}
                            {formConsentOptions.consentToProcessCheckboxLabel && (
                                <div
                                    className='gap-15 flex flex-row items-center mb-25'>
                                    <input
                                        className='bg-surface-raised border-none'
                                        id='consentToProcess'
                                        required={formConsentOptions.type === 'explicit_consent_to_process'}
                                        type='checkbox'
                                        {...register('consentToProcess')} />
                                    <label
                                        className='inline-block border-0 p-0 type-style-body'
                                        htmlFor='consentToProcess'>{formConsentOptions.consentToProcessCheckboxLabel}
                                        <sup className='text-brand-500 ml-5'>*</sup>
                                    </label>
                                </div>
                            )}
                            {formConsentOptions?.privacyText && (
                                <div
                                    className='w-full rich-text type-style-body mb-25'
                                    dangerouslySetInnerHTML={{ __html: formConsentOptions.privacyText }} />
                            )}
                        </div>
                    )}

                    {/* Hidden UTM fields */}
                    {utmFields?.length > 0 && (
                        utmFields.map((field) => {
                            return (
                                <input
                                    className='sr-only'
                                    key={field.name}
                                    type='hidden'
                                    value={utmParams?.[field?.name] ?? ''}
                                    {...register(`${field.objectTypeId}/${field.name}`, { required: false })} />
                            );
                        })
                    )}
                    <div className='flex pl-8'>
                        <SubmitButton className='button button--primary rounded-button text-white type-style-eyebrow px-20 py-15 ml-auto'>
                            {T('FORM_SUBMISSION_BTN_LABEL')}
                        </SubmitButton>
                    </div>
                </form>
            )}

            {/* Is an email form */}
            {(!formId && formEmail) && (
                <form
                    className='flex flex-wrap'
                    onSubmit={handleSubmit(onEmailSubmit)}>
                    <div className='w-full md:w-1/2 px-8 mb-25'>
                        <Label
                            htmlFor='firstname'
                            required={true}>
                            {T('FORM_FIRST_NAME_FIELD_LABEL')}
                        </Label>
                        <Input
                            placeholder={T('Jane')}
                            {...register('firstname', { required: true })} />
                    </div>

                    <div className='w-full md:w-1/2 px-8 mb-25'>
                        <Label
                            htmlFor='lastname'
                            required={true}>
                            {T('FORM_LAST_NAME_FIELD_LABEL')}
                        </Label>
                        <Input
                            placeholder={T('Smith')}
                            {...register('lastname', { required: true })} />
                    </div>

                    <div className='w-full md:w-1/2 px-8 mb-25'>
                        <Label
                            htmlFor='email'
                            required={true}
                            type='email'>
                            {T('FORM_EMAIL_ADDRESS_FIELD_LABEL')}
                        </Label>
                        <Input
                            placeholder={T('jane@example.com')}
                            {...register('email', { required: true })} />
                    </div>

                    <div className='w-full md:w-1/2 px-8 mb-25'>
                        <Label
                            htmlFor='phone'
                            required={true}>
                            {T('FORM_PHONE_NUMBER_FIELD_LABEL')}
                        </Label>
                        <Input
                            placeholder={T('+5 (555) 555-5555')}
                            {...register('phone', { required: true })} />
                    </div>

                    <div className='w-full px-8 mb-25'>
                        <Label
                            htmlFor='message'
                            required={true}>
                            {T('FORM_MESSAGE_BODY_FIELD_LABEL')}
                        </Label>
                        <Textarea
                            placeholder={T('FORM_MESSAGE_BODY_PLACEHOLDER')}
                            rows={4}
                            {...register('message', { required: true })} />
                    </div>

                    <div className='flex flex-wrap justify-between items-center w-full sm:text-right'>
                        {fieldMissing &&
                            <Warning className='flex items-center type-style-eyebrow mb-25 mr-20'>
                                {T('FORM_ERROR_ONE_OR_MORE_REQUIRED_FIELDS')}
                            </Warning>
                        }

                        {(!fieldMissing && errorMessage) &&
                            <Warning className='flex items-center type-style-eyebrow mb-25 mr-20 '>
                                {errorMessage}
                            </Warning>
                        }

                        {(!fieldMissing && !errorMessage && !isDirty && message) &&
                            <div className='flex items-center type-style-eyebrow mb-25 mr-20'>
                                {message}
                            </div>
                        }
                    </div>


                    <div className='flex pl-8 gap-x-8'>
                        <SubmitButton className='button button--primary rounded-button text-white type-style-eyebrow px-20 py-15 ml-auto h-max'>
                            <span className='button__label--animate'>
                                <span>
                                    {T('FORM_SUBMISSION_BTN_LABEL')}
                                </span>
                            </span>
                        </SubmitButton>
                        <div
                            className='g-recaptcha'
                            data-sitekey='6LcgR1ksAAAAAJZkReC9pRRpkOthcTLcBzkkVjF0' />
                    </div>
                </form>
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ContactForm.propTypes = {
    formConsentOptions: PropTypes.object,
    formEmail: PropTypes.string,
    formFields: PropTypes.array,
    formId: PropTypes.string,
    slug: PropTypes.string,
    title: PropTypes.string,
};

export default ContactForm;
